import { useState, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { axiosClientAdmin } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { User } from 'lucide-react';
import { ColumnDef } from '@tanstack/react-table';
import { FullPageTable } from '@/components/tables/FullPageTable';
import { createTextColumn, createLinkColumn } from '@/components/tables/column-helpers';
import { FilterFieldConfig } from '@/hooks/useJsonFilters';
import { SearchCompanyAgentsQuery } from '@/lib/api/monitoring/agents';
import { AddTeamMemberModal } from './AddTeamMemberModal';
import { CreateUserModal } from './CreateUserModal';
import { MonitoringOfficerDto } from '@/lib/api/types.gen';
import { Comparator, FilterOperator, FilterGroup, SortOrder } from '@/lib/api/types.gen';
import { Spinner } from '@/components/ui/spinner';

interface TeamAgentsTabProps {
  tenantId: string;
  teamId: string;
}

interface TeamAgentTableData {
  id: string;
  userId: string;
  name: string;
  email: string;
  original: MonitoringOfficerDto;
}

async function removeAgentFromTeam({
  teamId,
  userId,
}: {
  teamId: string;
  userId: string;
}): Promise<void> {
  await axiosClientAdmin.delete(`/monitoring/companies/${teamId}/users/${userId}`);
}

const teamAgentsFilterConfig: FilterFieldConfig[] = [
  {
    type: 'text',
    name: 'name',
    label: 'Search Members',
    field: 'name',
    comparator: Comparator.ILike,
    placeholder: 'Search team members by name or email...',
    isSearch: true,
  },
];

function extractTeamAgentTableData(agents: MonitoringOfficerDto[]): TeamAgentTableData[] {
  return agents
    .filter(agent => agent.id && agent.userId && agent.name && agent.email)
    .map(agent => ({
      id: agent.id!,
      userId: agent.userId!,
      name: agent.name!,
      email: agent.email!,
      original: agent,
    }));
}

export function TeamAgentsTab({ teamId }: TeamAgentsTabProps) {
  const queryClient = useQueryClient();
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
  const [isRemoveConfirmOpen, setIsRemoveConfirmOpen] = useState(false);
  const [agentToRemove, setAgentToRemove] = useState<{ id: string; name: string } | null>(null);
  const [newUserDefaults, setNewUserDefaults] = useState<{
    firstName: string;
    lastName: string;
    email: string;
  } | null>(null);
  const { toast } = useToast();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState<FilterGroup[]>([]);
  const [sortField] = useState('name');
  const [sortDirection] = useState(SortOrder.Ascending);

  const removeAgentMutation = useMutation<void, Error, { userId: string }>({
    mutationFn: ({ userId }: { userId: string }) => removeAgentFromTeam({ teamId, userId }),
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Agent removed from team successfully.',
      });
      queryClient.invalidateQueries({
        queryKey: ['monitoring.company.agents.search'],
      });
      setIsRemoveConfirmOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to remove agent from team.',
        variant: 'destructive',
      });
      setIsRemoveConfirmOpen(false);
    },
  });

  const handleMemberAdded = () => {
    queryClient.invalidateQueries({
      queryKey: ['monitoring.company.agents.search'],
    });
    setIsAddMemberModalOpen(false);
  };

  const handleUserCreatedAndAdded = () => {
    queryClient.invalidateQueries({
      queryKey: ['monitoring.company.agents.search'],
    });
    setIsCreateUserModalOpen(false);
  };

  const queryInput = useMemo(() => ({
    companyId: teamId,
    pageNumber: page,
    pageSize: pageSize,
    sortBy: sortField,
    sortDir: sortDirection,
    filterFilterGroups: filters
  }), [teamId, page, pageSize, sortField, sortDirection, filters]);

  const agentsQuery = useQuery({
    ...SearchCompanyAgentsQuery(queryInput),
    enabled: teamId != null,
  });

  const teamAgentData = useMemo(() => {
    if (!agentsQuery.data?.rows) return [];
    return extractTeamAgentTableData(agentsQuery.data.rows);
  }, [agentsQuery.data?.rows]);

  const handleFiltersChange = useCallback(({
    filterFilterGroups,
  }: {
    filterOperator: FilterOperator;
    filterFilterGroups: FilterGroup[];
    values: Record<string, string | Date | undefined>;
  }) => {
    const filtersChanged = JSON.stringify(filterFilterGroups) !== JSON.stringify(filters);
    setFilters(filterFilterGroups);
    if (filtersChanged) {
      setPage(1);
    }
  }, [filters]);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  }, []);

  const teamAgentColumns: ColumnDef<TeamAgentTableData>[] = useMemo(() => [
    createLinkColumn<TeamAgentTableData>({
      id: 'name',
      label: 'Name',
      accessorKey: 'name',
      enableHiding: false,
      sortable: true,
      getHref: (row) => `/users/${row.userId}`,
    }),
    createTextColumn<TeamAgentTableData>({
      id: 'email',
      label: 'Email',
      accessorKey: 'email',
      sortable: true,
      cell: (value) => (
        <span className="text-white/70">{value}</span>
      ),
    }),
    {
      id: 'actions',
      enableHiding: false,
      meta: {
        label: 'Actions',
        align: 'right',
      },
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex gap-2 justify-end">
          <Button
            variant="outline"
            size="sm"
            className="border-red-500 text-red-500 hover:bg-red-500/10 hover:text-red-500"
            onClick={() => {
              setAgentToRemove({ id: row.original.userId, name: row.original.name });
              setIsRemoveConfirmOpen(true);
            }}
            disabled={
              removeAgentMutation.isPending &&
              removeAgentMutation.variables?.userId === row.original.userId
            }
          >
            Remove
          </Button>
        </div>
      ),
    },
  ], [removeAgentMutation.isPending, removeAgentMutation.variables]);

  return (
    <div className="flex flex-col mt-8 max-w-[1280px] mx-auto border rounded-lg">
      <div className="flex items-center justify-between border-b p-4">
        <h2 className="text-xl font-semibold">Team Members</h2>
        <Button
          onClick={() => setIsAddMemberModalOpen(true)}
          variant="default"
        >
          <User className="mr-2 h-4 w-4" />
          Add Member
        </Button>
      </div>
      
      <div className="flex-1">
        <FullPageTable
          title=""
          columns={teamAgentColumns}
          data={teamAgentData}
          loading={agentsQuery.isLoading && !agentsQuery.data}
          error={agentsQuery.error}
          page={page}
          pageSize={pageSize}
          totalCount={agentsQuery.data?.totalRows}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={[5, 10, 20, 100]}
          filterConfig={teamAgentsFilterConfig}
          onFiltersChange={handleFiltersChange}
          stateKey="team-agents-table"
          emptyMessage="No team members found"
        />
      </div>

      <AddTeamMemberModal
        isOpen={isAddMemberModalOpen}
        onClose={() => setIsAddMemberModalOpen(false)}
        teamId={teamId}
        onMemberAdded={handleMemberAdded}
        onInitiateCreateUser={({ firstName, lastName, email }) => {
          setIsAddMemberModalOpen(false);
          setNewUserDefaults({ firstName, lastName, email });
          setIsCreateUserModalOpen(true);
        }}
      />
      <CreateUserModal
        isOpen={isCreateUserModalOpen}
        onClose={() => {
          setIsCreateUserModalOpen(false);
          setNewUserDefaults(null);
        }}
        teamId={teamId}
        onUserCreatedAndAdded={handleUserCreatedAndAdded}
        initialFirstName={newUserDefaults?.firstName}
        initialLastName={newUserDefaults?.lastName}
        initialEmail={newUserDefaults?.email}
      />

      {agentToRemove && (
        <Dialog open={isRemoveConfirmOpen} onOpenChange={setIsRemoveConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Removal</DialogTitle>
            </DialogHeader>
            <DialogDescription>
              Are you sure you want to remove "{agentToRemove.name}" from this team? This action
              cannot be undone.
            </DialogDescription>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsRemoveConfirmOpen(false)}
                disabled={removeAgentMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  if (agentToRemove) {
                    removeAgentMutation.mutate({ userId: agentToRemove.id });
                  }
                }}
                disabled={removeAgentMutation.isPending}
              >
                {removeAgentMutation.isPending &&
                removeAgentMutation.variables?.userId === agentToRemove?.id ? (
                  <Spinner size="small" className="mr-2" />
                ) : null}
                Remove
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
